/* eslint-disable max-lines-per-function */
import { isNumber } from 'lodash';
import { useMemo } from 'react';

import { TaxRate } from '@dreryk/egabinet-private-medical-service-frontend-api';
import { PractitionerId } from '@dreryk/egabinet-personnel-published-language-api';

import { getCustomFormattingContext, priceFormatter } from '@dreryk/react-common';
import {
   createDynamicColumnsWidth,
   HorizontalAlign,
   InteractiveTable,
   InteractiveTableColumnConfiguration,
   JustifyContent,
} from '@dreryk/react-components';

import { invoiceCreateModalTranslationKeys } from '../../../translations';
import { invoiceTypeVisitor } from '../InvoiceCreateForm.utils';

import { InvoiceItemsTableBean } from './InvoiceItemsTable.types';
import { NEW_ROW_EMPTY_VALUE_PLACEHOLDER } from './InvoiceItemsTable.utils';
import { InvoiceItemsTableMedicalServiceNameCell } from './InvoiceItemsTableMedicalServiceNameCell';
import { InvoiceItemsTableQuantityCell } from './InvoiceItemsTableQuantityCell';
import { InvoiceItemsTableTaxRateCell } from './InvoiceItemsTableTaxRateCell';
import { InvoiceItemsTableUnitCell } from './InvoiceItemsTableUnitCell';
import { InvoiceItemsTableUnitPriceCell } from './InvoiceItemsTableUnitPriceCell';

type InvoiceItemsTableColumnConfiguration = InteractiveTableColumnConfiguration<InvoiceItemsTableBean>;

type UseInvoiceItemsColumnsOptions = {
   taxRates: TaxRate[];
   receiptAvailableForInvoice: boolean;
   practitionerId?: PractitionerId;
};

export const useInvoiceItemsColumns = (
   options: UseInvoiceItemsColumnsOptions
): InvoiceItemsTableColumnConfiguration[] => {
   return useMemo(() => {
      const columnWidths = createDynamicColumnsWidth({
         ordinalNumber: '48px',
         name: '100%',
         quantity: '60px',
         unit: '88px',
         unitPrice: '124px',
         taxRate: '52px',
         options: '24px',
      });

      return [
         {
            name: 'ordinalNumber',
            label: invoiceCreateModalTranslationKeys.table.ordinalNumber,
            width: columnWidths.ordinalNumber,
            renderer: ({ index }) => {
               return (
                  <InteractiveTable.TextViewCell
                     defaultValue={`${index + 1}.`}
                     index={index}
                     name={'ordinalNumber'}
                     testId={'ordinalNumber'}
                  />
               );
            },
         },
         {
            name: 'name',
            label: invoiceCreateModalTranslationKeys.table.name.label,
            width: columnWidths.name,
            renderer: ({ bean, index }) => {
               return (
                  <InvoiceItemsTableMedicalServiceNameCell
                     defaultValue={bean.name}
                     index={index}
                     name={'name'}
                     testId={'name'}
                     taxRates={options.taxRates}
                     practitionerId={options.practitionerId}
                  />
               );
            },
         },
         {
            name: 'quantity',
            label: invoiceCreateModalTranslationKeys.table.quantity,
            width: columnWidths.quantity,
            renderer: ({ bean, index }) => {
               return invoiceTypeVisitor(options.receiptAvailableForInvoice)({
                  withReceipt: () => (
                     <InteractiveTable.TextViewCell
                        defaultValue={bean.quantity ?? NEW_ROW_EMPTY_VALUE_PLACEHOLDER}
                        testId={'quantity'}
                        index={index}
                        name={'quantity'}
                     />
                  ),
                  default: () => (
                     <InvoiceItemsTableQuantityCell
                        defaultValue={bean.quantity}
                        index={index}
                        name={'quantity'}
                        testId={'quantity'}
                     />
                  ),
               });
            },
         },
         {
            name: 'unit',
            label: invoiceCreateModalTranslationKeys.table.unit,
            width: columnWidths.unit,
            renderer: ({ index }) => {
               return (
                  <InvoiceItemsTableUnitCell defaultValue={'SERVICE'} index={index} name={'unit'} testId={'unit'} />
               );
            },
         },
         {
            name: 'unitPrice',
            label: invoiceCreateModalTranslationKeys.table.unitPrice,
            width: columnWidths.unitPrice,
            textAlign: HorizontalAlign.RIGHT,
            justifyContent: JustifyContent.END,
            renderer: ({ bean, index }) => {
               return invoiceTypeVisitor(options.receiptAvailableForInvoice)({
                  withReceipt: () => (
                     <InteractiveTable.TextViewCell
                        defaultValue={bean.unitPrice}
                        testId={'unitPrice'}
                        index={index}
                        name={'unitPrice'}
                        renderer={(value) =>
                           isNumber(value)
                              ? priceFormatter(value, getCustomFormattingContext({ options: { currency: 'PLN' } }))
                              : NEW_ROW_EMPTY_VALUE_PLACEHOLDER
                        }
                     />
                  ),
                  default: () => (
                     <InvoiceItemsTableUnitPriceCell
                        defaultValue={bean.unitPrice}
                        index={index}
                        name={'unitPrice'}
                        testId={'unitPrice'}
                     />
                  ),
               });
            },
         },
         {
            name: 'taxRate',
            label: invoiceCreateModalTranslationKeys.table.taxRate,
            width: columnWidths.taxRate,
            renderer: ({ bean, index }) => {
               return (
                  <InvoiceItemsTableTaxRateCell
                     defaultValue={bean.taxRate}
                     index={index}
                     name={'taxRate'}
                     testId={'taxRate'}
                  />
               );
            },
         },
         {
            name: 'options',
            width: columnWidths.options,
            renderer: ({ bean, index, rowsCount }) => {
               return invoiceTypeVisitor(options.receiptAvailableForInvoice)({
                  withReceipt: () => (
                     <InteractiveTable.CustomCell
                        defaultValue={bean.invoiceParameters}
                        index={index}
                        name={'invoiceParameters'}
                        testId={'invoiceParameters'}
                        compact
                     >
                        {() => <></>}
                     </InteractiveTable.CustomCell>
                  ),
                  default: () => (
                     <>
                        <InteractiveTable.DeleteRowCell index={index} disabled={rowsCount <= 1} />
                        <InteractiveTable.CustomCell
                           defaultValue={bean.invoiceParameters}
                           index={index}
                           name={'invoiceParameters'}
                           testId={'invoiceParameters'}
                           compact
                        >
                           {() => <></>}
                        </InteractiveTable.CustomCell>
                     </>
                  ),
               });
            },
         },
      ];
   }, [options.taxRates, options.receiptAvailableForInvoice, options.practitionerId]);
};
